<script>
  import { onMount } from 'svelte';
  import { scaleLinear, scaleSqrt } from 'd3-scale';
  import { calendarURL, debounce, getStickerSize } from './settings';
  import IgnorantBar from './IgnorantBar.svelte';
  import Icons from './Icons.svelte';
  import NotePercent from './NotePercent.svelte';

  const weekday = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

  let calendar = [];
  let loading = true;
  let error = false;

  let minCount = 0;
  let maxCount = 0;

  let selectedDay = -1;

  let iconsScale = () => 0;
  let barsScale = () => 0;

  const draw = () => {
    iconsScale = scaleSqrt()
      .range(getStickerSize())
      .domain([0, 1]);
    calendar = calendar.slice(0);
    barsScale = scaleLinear()
      .range([0, 90])
      .domain([0, maxCount]);
  };

  const debouncedSetWindowWidth = debounce(draw, 300);

  const selectDay = clicked => {
    if (clicked === selectedDay) {
      selectedDay = -1;
    } else {
      selectedDay = clicked;
    }
    setTimeout(
      () => {
        const element = document.querySelector(`#day-${clicked}`);
        const topPos = element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: topPos,
          behavior: 'smooth',
        });
      },
      500
    );
  };

  onMount(async () => {
    try {
      const response = await fetch(calendarURL);
      const data = await response.json();
      calendar = data?.data || [];
      minCount = Math.min(...calendar.map(d => d.count));
      maxCount = Math.max(...calendar.map(d => d.count));
      calendar.forEach((c) => {
        c.percent = {
          positive: c.positive / c.count,
          negative: c.negative / c.count,
          neutral: c.neutral / c.count,
        };
        c.meloni.percent = {
          global: c.meloni.count / c.count,
          positive: c.meloni.positive / c.count,
          negative: c.meloni.negative / c.count,
          neutral: c.meloni.neutral / c.count,
        };
        c.schlein.percent = {
          global: c.schlein.count / c.count,
          positive: c.schlein.positive / c.count,
          negative: c.schlein.negative / c.count,
          neutral: c.schlein.neutral / c.count,
        };
      })
      draw();
      loading = false;
    } catch(e) {
      console.warn(e);
      loading = false;
      error = true;
    }
    window.addEventListener('resize', debouncedSetWindowWidth);		
		return () => {
			window.removeEventListener('resize', debouncedSetWindowWidth);
		}
  });
</script>

<div id="calendar" class="{loading ? 'loading' : ''}">
  {#if error}
    <p class="error">C'è stato un problema nel caricamento dei dati, per favore riprova più tardi.</p>
  {:else}
    <div class="calendar--content">
      <div class="calendar--text">
        <p>Cerchiamo di capire come il rapporto tra gli articoli dedicati alle nostre protagoniste si sia evoluto nel tempo, anche tenendo conto di ciò che è successo giorno per giorno.</p>
      </div>
      <div class="calendar--chart">
        {#each calendar as c, index}
        <div class="calendar--day {selectedDay === index ? 'selected' : ''}" on:click={() => selectDay(index)} on:keyup={e => { if (e.key.toLowerCase() === 'enter') selectDay(index); }} role="button" tabindex="0" id="day-{index}">
          <div class="day--title">
            <h4><span>{weekday[new Date(c.date).getDay()]}</span> <span>{index === 0 || new Date(c.date).getDate() === 1 ? 
              new Intl.DateTimeFormat('it-IT', { dateStyle: 'short' }).format(new Date(c.date)) :
              new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: '2-digit' }).format(new Date(c.date))
            }</span></h4>
          </div>
          <div class="day--bar--wrapper">
            <div class="day--bar" style="width: {barsScale(c.count)}%;">
              <IgnorantBar bars={[{ w: c.meloni.percent.global * 100, c: 'meloni' }, { w: c.schlein.percent.global * 100, c: 'schlein' }]} labels={[{ c: 'meloni', l: `${index === 0 ? 'Meloni ' : ''}${new Intl.NumberFormat('it-IT', { style: 'percent' }).format(c.meloni.percent.global)}`}, { c: 'overlap', l: new Intl.NumberFormat('it-IT', { style: 'percent' }).format((c.meloni.percent.global + c.schlein.percent.global) - 1) }, { c: 'schlein', l: `${index === 0 ? 'Schlein ' : ''}${new Intl.NumberFormat('it-IT', { style: 'percent' }).format(c.schlein.percent.global)}`}]} />
              <div class="day--total">{c.count > 10000 ? new Intl.NumberFormat('it-IT', { notation: 'compact' }).format(c.count) : new Intl.NumberFormat('it-IT', {}).format(c.count)}{index === 0 ? ' articoli totali' : ''}</div>
            </div>
          </div>
          <div class="calendar--day--icons {selectedDay === index ? 'visible' : ''}">
            <Icons data={c} scale={iconsScale} />
          </div>
        </div>
        {/each}
      </div>
      <NotePercent />
    </div>
  {/if}
</div>

<style>
#calendar {
  display: block;
  height: auto;
  margin: 0 20px;
  opacity: 1;
  padding: 10vh 0;
  transition: opacity .5s ease-in-out;
  width: auto;
}

#calendar.loading {
  opacity: 0;
}

.calendar--day {
  cursor: pointer;
  margin-bottom: 40px;
}

.day--title {
  margin-bottom: 20px;
}

.day--title h4 {
  text-align: left;
}

.day--title h4::before {
  border: 1px solid var(--dark);
  border-radius: 50%;
  content: '+';
  display: inline-block;
  height: 14px;
  line-height: 14px;
  margin-right: 5px;
  text-align: center;
  vertical-align: text-top;
  width: 14px;
}

.calendar--day.selected .day--title h4::before {
  content: '-';
}

.day--bar {
  position: relative;
}

.day--total {
  font-family: 'Fira Mono', monospace;
  font-size: 12px;
  font-weight: 500;
  left: 100%;
  position: absolute;
  text-align: left;
  text-transform: uppercase;
  top: 50%;
  transform: translate3d(5px, -50%, 0);
}

.calendar--day--icons {
    display: none;
    flex: 0 0 100%;
    margin-top: 40px;
    opacity: 0;
    transition: opacity .5s ease-in-out;
}

.calendar--day--icons.visible {
  display: block;
  opacity: 1;
}

@media screen and (min-width:768px) {
  #calendar {
      max-width: 960px;
    }
}

@media screen and (min-width:1024px) {
  #calendar {
      margin: 0 auto;
  }

  .calendar--day {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .day--title {
    flex: 0 0 120px;
    margin: 0;
    padding-right: 10px;
  }

  .day--title h4 {
    text-align: right;
  }

  .day--title h4::before {
    margin: 0;
  }

  .day--title span {
    display: block;
  }

  .day--bar--wrapper {
    flex: 1 0 auto;
    margin: 0;
  }
}
</style>